// SOK-04: modo mala conexión.
//
// `navigator.onLine` solo dice si hay interfaz de red, no si hay internet ni si kiosk-backend
// responde. Por eso se suma un latido HTTP a un endpoint liviano (getServerTimestamp). Con eso se
// decide si se puede iniciar un cobro: el catálogo sigue funcionando sin red porque Firestore usa
// caché persistente, pero el cobro depende de HTTP a Cloud Run y del POS.
//
// Solo bloquea el inicio de un cobro. Un cobro que ya empezó (cuenta regresiva o "Revisando tu
// pago") no se toca: el POS puede tener la tarjeta y el polling ya reintenta solo (KIO-03).

export const CONNECTIVITY = Object.freeze({ ONLINE: 'online', DEGRADED: 'degraded', OFFLINE: 'offline' });

export const HEARTBEAT_URL = 'https://getservertimestamp-x3herayjcq-uc.a.run.app';

// Los campos viven en style.global.connectivity y los edita kiosk-dashboard, que se publica por
// separado. Igual que en paymentWait.js, un valor que falta o no sirve cae al default.
export const CONNECTIVITY_DEFAULTS = Object.freeze({
  heartbeatIntervalSeconds: 10,
  heartbeatTimeoutSeconds: 3,
  degradedLatencySeconds: 2,
  failuresToOffline: 2,
  // Cuánto espera el modal a que vuelva la red para confirmar un cobro cortado antes de decir
  // "No pudimos confirmar tu pago". El cobro sigue anotado y se confirma igual al volver la red.
  paymentRecoveryWaitSeconds: 180,
});

const LIMITS = {
  heartbeatIntervalSeconds: [5, 120],
  heartbeatTimeoutSeconds: [1, 30],
  degradedLatencySeconds: [0.5, 30],
  failuresToOffline: [1, 10],
  paymentRecoveryWaitSeconds: [30, 900],
};

const toNumber = (value) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value.trim() !== '') return Number(value);
  return NaN;
};

/**
 * Lee la configuración de conectividad desde el documento de estilos, con defaults.
 *
 * @param {object} style - Documento config/styles del cliente (puede venir vacío o incompleto).
 * @returns {{heartbeatIntervalSeconds: number, heartbeatTimeoutSeconds: number, degradedLatencySeconds: number, failuresToOffline: number, paymentRecoveryWaitSeconds: number}}
 */
export function getConnectivityConfig(style) {
  const source = style?.global?.connectivity || {};
  const config = {};
  for (const [key, fallback] of Object.entries(CONNECTIVITY_DEFAULTS)) {
    const value = toNumber(source[key]);
    const [min, max] = LIMITS[key];
    config[key] = Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : fallback;
  }
  config.failuresToOffline = Math.round(config.failuresToOffline);
  // Una latencia "lenta" mayor que el timeout nunca se vería: el latido ya habría fallado.
  config.degradedLatencySeconds = Math.min(config.degradedLatencySeconds, config.heartbeatTimeoutSeconds);
  return config;
}

/**
 * Estado según el último latido.
 *
 * - Sin interfaz de red: `offline` de inmediato, sin esperar latidos.
 * - `failuresToOffline` latidos fallidos seguidos: `offline`.
 * - Un fallo suelto o un latido lento: `degraded` (no bloquea cobros).
 *
 * @param {object} params
 * @param {boolean} params.navigatorOnline
 * @param {number} params.failures - Latidos fallidos seguidos.
 * @param {number | null} params.latencyMs - Latencia del último latido exitoso.
 * @param {{degradedLatencySeconds: number, failuresToOffline: number}} params.config
 * @returns {'online' | 'degraded' | 'offline'}
 */
export function getConnectivityStatus({ navigatorOnline, failures, latencyMs, config }) {
  if (!navigatorOnline) return CONNECTIVITY.OFFLINE;
  if (failures >= config.failuresToOffline) return CONNECTIVITY.OFFLINE;
  if (failures > 0) return CONNECTIVITY.DEGRADED;
  if (latencyMs != null && latencyMs > config.degradedLatencySeconds * 1000) return CONNECTIVITY.DEGRADED;
  return CONNECTIVITY.ONLINE;
}

/** Se puede iniciar un cobro nuevo. `degraded` cobra igual: es lento, no está caído. */
export const canStartPayment = (status) => status !== CONNECTIVITY.OFFLINE;

/**
 * Latido: GET al endpoint con su propio límite. Nunca rechaza.
 *
 * @returns {Promise<{ok: boolean, latencyMs: number}>}
 */
export async function heartbeat({ url = HEARTBEAT_URL, timeoutMs, fetchFn = fetch, now = () => Date.now() }) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const startedAt = now();
  try {
    const response = await fetchFn(url, { method: 'GET', mode: 'cors', cache: 'no-store', signal: controller.signal });
    return { ok: !!response?.ok, latencyMs: now() - startedAt };
  } catch {
    return { ok: false, latencyMs: now() - startedAt };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Monitor de conectividad: combina `navigator.onLine` con el latido periódico y avisa cada cambio
 * de estado con cuánto duró el anterior. No depende de React para poder probarlo con timers falsos.
 *
 * @param {object} options
 * @param {object} options.config - getConnectivityConfig().
 * @param {(change: {status: string, previous: string, durationMs: number, latencyMs: number | null}) => void} options.onChange
 * @param {() => boolean} [options.isNavigatorOnline]
 * @param {(timeoutMs: number) => Promise<{ok: boolean, latencyMs: number}>} [options.ping]
 * @param {() => number} [options.now]
 * @param {EventTarget} [options.target] - Donde llegan los eventos `online`/`offline` (window).
 */
export function createConnectivityMonitor({
  config,
  onChange,
  isNavigatorOnline = () => navigator.onLine !== false,
  ping = (timeoutMs) => heartbeat({ timeoutMs }),
  now = () => Date.now(),
  target = typeof window !== 'undefined' ? window : null,
}) {
  let failures = 0;
  let latencyMs = null;
  let status = isNavigatorOnline() ? CONNECTIVITY.ONLINE : CONNECTIVITY.OFFLINE;
  let since = now();
  let timer = null;
  let running = false;
  // Cada latido lleva un número; si llega uno viejo después de otro más nuevo, se ignora.
  let sequence = 0;

  const update = () => {
    const next = getConnectivityStatus({ navigatorOnline: isNavigatorOnline(), failures, latencyMs, config });
    if (next === status) return;
    const at = now();
    const change = { status: next, previous: status, durationMs: at - since, latencyMs };
    status = next;
    since = at;
    onChange?.(change);
  };

  const beat = async () => {
    if (!running) return;
    const id = ++sequence;
    if (!isNavigatorOnline()) {
      update();
      return;
    }
    const result = await ping(config.heartbeatTimeoutSeconds * 1000);
    if (!running || id !== sequence) return;
    if (result.ok) {
      failures = 0;
      latencyMs = result.latencyMs;
    } else {
      failures += 1;
    }
    update();
  };

  const schedule = () => {
    clearInterval(timer);
    timer = setInterval(beat, config.heartbeatIntervalSeconds * 1000);
  };

  // Al volver la interfaz se late de inmediato: la red puede estar de vuelta antes del próximo latido.
  const handleOnline = () => {
    beat();
    schedule();
  };
  const handleOffline = () => update();

  return {
    start() {
      if (running) return;
      running = true;
      target?.addEventListener('online', handleOnline);
      target?.addEventListener('offline', handleOffline);
      beat();
      schedule();
    },
    stop() {
      running = false;
      clearInterval(timer);
      timer = null;
      target?.removeEventListener('online', handleOnline);
      target?.removeEventListener('offline', handleOffline);
    },
    getStatus: () => status,
  };
}
